import { TextField, SxProps, Theme } from '@mui/material';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import NumberFormat, { NumberFormatProps } from 'react-number-format';

type NumberTextFieldProps = {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  label: string;
  prefix?: string;
  suffix?: string;
  rules?: Object;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  disabled?: boolean;
  defaultValue?: string | number;
  onChangeValue?: (value: number) => void;
  triggerChangeOnBlur?: boolean;
  percent?: boolean;
  fullWidth?: boolean;
  fixedDecimalScale?: boolean;
  sx?: SxProps<Theme>;
  decimalScale?: number;
  isAllowed?: NumberFormatProps['isAllowed'];
  formatDecimalsOnBlur?: boolean;
};

export function NumberTextField({
  name,
  label,
  prefix,
  suffix,
  rules,
  error,
  helperText,
  readOnly,
  disabled,
  defaultValue,
  onChangeValue,
  triggerChangeOnBlur,
  percent,
  fullWidth = true,
  fixedDecimalScale,
  sx,
  decimalScale,
  isAllowed,
  formatDecimalsOnBlur,
}: NumberTextFieldProps) {
  const {
    field: { ref, onChange, value },
  } = useController({
    name,
    rules,
    defaultValue,
  });
  if (percent) {
    suffix = '%';
  }

  const fieldValue = percent ? value * 100 : value;

  const [triggerDecimalChange, setTriggerDecimalChange] = useState(false);

  return (
    <NumberFormat
      onFocus={formatDecimalsOnBlur ? () => setTriggerDecimalChange(false) : undefined}
      id={name}
      prefix={prefix}
      suffix={suffix}
      sx={sx}
      thousandSeparator={true}
      customInput={TextField}
      onValueChange={v => {
        const numberValue = v.floatValue || Number(v.value);
        const newValue = percent ? numberValue / 100 : numberValue;
        onChange(newValue);
        if (!triggerChangeOnBlur) {
          onChangeValue?.(newValue);
        }
      }}
      onBlur={() => {
        if (triggerChangeOnBlur) {
          const newValue = percent ? Number(value) / 100 : Number(value);
          onChangeValue?.(newValue);
        }
        if (formatDecimalsOnBlur) setTriggerDecimalChange(true);
      }}
      value={fieldValue}
      fullWidth={fullWidth}
      decimalScale={decimalScale ?? 2}
      fixedDecimalScale={fixedDecimalScale || triggerDecimalChange}
      label={label}
      error={error}
      helperText={helperText}
      variant='outlined'
      InputProps={{
        readOnly,
      }}
      disabled={disabled}
      getInputRef={ref}
      isAllowed={isAllowed}
    />
  );
}
